/* eslint-disable no-shadow */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable spaced-comment */
/* eslint-disable react/require-default-props */
import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Text,
  Button,
  Icon,
  useBreakpointValue,
  useDisclosure,
  Tooltip,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RiAddLine } from 'react-icons/ri';
import { useLoading, Oval } from '@agney/react-loading';

import getValidationErrors from '../../../../utils/getValidationErrors';
import Input from '../../../Global/Input';
import TextArea from '../../../Global/TextArea';
import Select from '../../../Global/SelectRelease';
import { ModalComponent } from '..';
import { IDriverKilometerCreate } from '../../../../services/driverKilometersService';

interface IModalProps {
  handleAddNewDriverKilometer: (data: IDriverKilometerCreate) => void;
}

export function ModalAddNewDriverKilometer({
  handleAddNewDriverKilometer,
}: IModalProps) {
  const formRef = useRef<FormHandles>(null);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  });

  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [reasonItem, setReason] = useState('');

  const [kmStartValue, setKmStartValue] = useState(0);
  const [kmEndValue, setKmEndValue] = useState(0);
  const [kmTraveledValue, setKmTraveledValue] = useState(0);

  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          date: Yup.date().required('Data é obrigatória'),
          km_start: Yup.number()
            .required('Km inicial é obrigatório')
            .min(0, 'Km inicial deve ser maior ou igual a zero'),
          km_end: Yup.number()
            .required('Km final é obrigatório')
            .min(0, 'Km final deve ser maior ou igual a zero')
            .moreThan(
              Yup.ref('km_start'),
              'Km final deve ser maior que km inicial',
            ),
          km_traveled: Yup.number()
            .required('Km percorrido é obrigatório')
            .positive('Km percorrido deve ser positivo'),
          destination: Yup.string().required('Destino é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const formatData: IDriverKilometerCreate = {
          km_end: Number(data.km_end),
          km_start: Number(data.km_start),
          km_traveled: Number(data.km_traveled),
          destination: String(data.destination),
          observation: data.observation ? String(data.observation) : '',
          reason: reasonItem,
          date: String(data.date),
          conductor_id: '', // Será preenchido no componente pai com o user.id
        };

        handleAddNewDriverKilometer(formatData);
        setKmTraveledValue(0);
        setKmStartValue(0);
        setKmEndValue(0);
        setReason('');
        onClose();

        setLoading(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setLoading(false);

          formRef.current?.setErrors(errors);

          return;
        }
        setKmTraveledValue(0);
        setKmStartValue(0);
        setKmEndValue(0);
        setLoading(false);
      }
    },
    [handleAddNewDriverKilometer, onClose, reasonItem],
  );

  const { containerProps, indicatorEl } = useLoading({
    loading,
    indicator: <Oval />,
  });

  const handleKmStart = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setKmStartValue(Number(e.target.value));
    },
    [],
  );

  const handleKmEnd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKmEndValue(Number(e.target.value));
  }, []);

  useEffect(() => {
    if (kmEndValue > kmStartValue) {
      setKmTraveledValue(kmEndValue - kmStartValue);
    } else {
      setKmTraveledValue(0);
    }
  }, [kmEndValue, kmStartValue]);

  useEffect(() => {
    if (!isOpen) {
      setKmTraveledValue(0);
      setKmStartValue(0);
      setKmEndValue(0);
      setReason('');
    }
  }, [isOpen]);

  const handleReason = useCallback((value: string) => {
    setReason(value);
  }, []);

  return (
    <>
      <Tooltip hasArrow label="Nova quilometragem">
        <Button
          as="a"
          size="sm"
          colorScheme="blue"
          fontWeight="medium"
          onClick={onOpen}
          cursor="pointer"
        >
          <Icon as={RiAddLine} fontSize="20" />
          {isWideVersion && <Text>Adicionar quilometragem</Text>}
        </Button>
      </Tooltip>
      <ModalComponent
        title="Nova quilometragem de condutor"
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalBody>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <p>Data</p>
            <Input type="datetime-local" name="date" />

            <p>Quilometragem inicial</p>
            <Input
              onChange={handleKmStart}
              value={kmStartValue}
              type="number"
              name="km_start"
              placeholder="Exemplo: 12000"
            />

            <p>Quilometragem final</p>
            <Input
              onChange={handleKmEnd}
              value={kmEndValue}
              type="number"
              name="km_end"
              placeholder="Exemplo: 12350"
            />

            <p>Quilômetros percorridos</p>
            <Input
              value={kmTraveledValue}
              type="number"
              name="km_traveled"
              placeholder="Calculado automaticamente"
              variant="filled"
              bg="gray.800"
              isReadOnly
            />

            <Select
              name="reason"
              label="Qual o motivo?"
              value={reasonItem}
              onChange={e => {
                handleReason(e.target.value);
              }}
              options={[
                {
                  value: 'Consultoria de venda',
                  label: 'Consultoria de venda',
                },
                { value: 'Entrega de produto', label: 'Entrega de produto' },
                {
                  value: 'Deslocamento a trabalho',
                  label: 'Deslocamento a trabalho',
                },
                {
                  value: 'Visita técnica',
                  label: 'Visita técnica',
                },
                {
                  value: 'Reunião externa',
                  label: 'Reunião externa',
                },
                {
                  value: 'Outros',
                  label: 'Outros',
                },
              ]}
            />

            <p>Qual o destino?</p>
            <Input name="destination" placeholder="Ex: São Paulo - Centro" />

            <p>Observações (opcional)</p>
            <TextArea
              name="observation"
              placeholder="Ex: reunião com cliente ABC."
            />

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Fechar
              </Button>
              <Button colorScheme="blue" type="submit">
                {loading ? (
                  <div {...containerProps} ref={componentRef}>
                    {indicatorEl}
                  </div>
                ) : (
                  'Salvar'
                )}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalComponent>
    </>
  );
}
